import { Button } from '@efexteam/components';
import { Actions, Can, Subjects } from '@efexteam/permission-manager';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ColorsPrimary100 } from '@efexteam/design-tokens';
import FEATURES_APP from '../configs/featureflags';
import leng from '../constants/leng';
import useBoolean from '../hooks/useBoolean';
import AccountStatusModal from '../modules/receivables/AccountStatusModal';
import { selectProfileData } from '../pages/profile/store/selectors';
import { selectSelectedLanguage } from '../pages/store/language/selectors';

interface OutletLayoutProps {
  title?: string;
  icon?: JSX.Element;
  children: React.ReactNode;
  titleClassName?: string;
  bgColor?: string;
  containerClassname?: string;
  fullWidth?: boolean;
}

const OutletLayout = ({
  children,
  title,
  icon,
  titleClassName,
  bgColor = `bg-[${ColorsPrimary100}]`,
  containerClassname,
  fullWidth = false,
}: OutletLayoutProps) => {
  const navigate = useNavigate();
  const selectLanguage = useSelector(selectSelectedLanguage);
  const userProfile = useSelector(selectProfileData);
  const location = useLocation();
  const [
    isAccountStatusModalOpen,
    { setFalse: hideAccountStatusModal, setTrue: showAccountStatusModal },
  ] = useBoolean(false);

  const openPopupDocumentKYB = () => {
    navigate('/onboarding');
  };

  const handleCloseAccountStatus = () => {
    hideAccountStatusModal();
  };

  const openPopupAccountState = () => {
    if (!userProfile?.isFullRegister) {
      openPopupDocumentKYB();
      return;
    }
    showAccountStatusModal();
  };

  const isValidRoute = () => ['/profile'].includes(location.pathname);

  return (
    <Box className={clsx('py-4 min-h-[90vh]', bgColor)}>
      <Box
        className={clsx(
          !fullWidth ? 'container px-5 mx-auto max-w-[1140px]' : ' px-5',
          containerClassname
        )}
      >
        <Box className={clsx(icon ? 'flex items-center' : undefined)}>
          <Box className="flex items-center w-full">
            <Box className="flex w-full">
              {icon && icon}
              <h1
                className={clsx(
                  'text-3xl m-0 text-btn-primary-fill mt-4 mb-6 text-[26px] font-bold',
                  titleClassName,
                  fullWidth ? 'md:pr-[30px]' : ''
                )}
              >
                {title}
              </h1>
            </Box>
            {FEATURES_APP.accountStatus &&
              isValidRoute() &&
              !userProfile?.isSubaccount && (
                <Can
                  action={Actions.MANAGE}
                  subject={Subjects.ACCOUNT_STATEMENTS}
                >
                  <Button
                    appearance="primary"
                    size="large"
                    className="md:mt-2 m-0 w-fit !min-w-[230px]"
                    onClick={openPopupAccountState}
                  >
                    {leng[selectLanguage.language].cashStatement}
                  </Button>
                </Can>
              )}
          </Box>
        </Box>
        {children}
      </Box>
      {isAccountStatusModalOpen && (
        <AccountStatusModal
          open={isAccountStatusModalOpen}
          onClose={handleCloseAccountStatus}
        />
      )}
    </Box>
  );
};

export default OutletLayout;
